import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Platform, Text, Pressable } from 'react-native';
import { Stack, XStack, SizableText } from 'tamagui';
import { mapColor } from 'utils/misc';
import { MainStyles } from 'resources/styles/MainStyles';
import { useColorModeValue } from 'utils/hooks';
import { isLoaded } from 'expo-font';
import { LoadFont } from 'controllers/FontController';
import { useSelector } from 'react-redux';
const HSKDash = /*#__PURE__*/React.memo(({
  skill,
  children
}) => {
  return /*#__PURE__*/React.createElement(SizableText, {
    height: 5,
    fontSize: "$lg",
    fontWeight: "bold",
    lineHeight: Platform.OS === 'web' ? 1 : 5,
    userSelect: Platform.OS === 'web' ? 'none' : null,
    textAlign: "center",
    color: MainStyles['HSK' + skill].backgroundColor
  }, children);
});
const TonedChars = props => {
  // Replace context with direct Redux selectors
  const uisettings = useSelector(state => state.uisettings);
  const languagesettings = useSelector(state => state.languagesettings);
  const colorsettings = useSelector(state => state.colorsettings);
  const [fontLoaded, setFontLoaded] = useState(false);
  const mainColor = useColorModeValue('black', '#FFF');
  const notClickable = useMemo(() => {
    const clickableArr = [', ', '，', ',', '。', '《', '》'];
    return char => clickableArr.includes(char) || props.disableTouch;
  }, [props.disableTouch]);
  useEffect(() => {
    if (Platform.OS === 'web') {
      checkFontLoadedAndLoad(uisettings.fontFamily);
    }
  }, [uisettings.fontFamily]);
  const checkFontLoadedAndLoad = async font_family => {
    if (font_family === 'Default') {
      setFontLoaded(true);
      return;
    }
    await LoadFont(font_family);
    const loaded = await isLoaded(font_family);
    if (!loaded) {
      await LoadFont(font_family);
    }
    setFontLoaded(true);
  };
  const fontFamilyDecider = useCallback(() => {
    if (props.char_type === 'pinyin' || uisettings.fontFamily === 'Default') {
      return null;
    }
    return uisettings.fontFamily;
  }, [props.char_type, uisettings.fontFamily]);
  const dashDisabled = useCallback(() => {
    if (props.char_type === 'pinyin' || props.dashDisabled) {
      return true;
    }
    return !colorsettings.globalDash;
  }, [props.char_type, props.dashDisabled, colorsettings.globalDash]);
  const mapToCharType = useCallback((char, charType) => {
    if (charType === 'pinyinMarks') {
      return !colorsettings.pinyinColors ? mainColor : mapColor(char, uisettings.darkMode ? 'dark' : 'light');
    } else if (charType === 'simplified' || charType === 'traditional') {
      return !colorsettings.charColors ? mainColor : mapColor(char, uisettings.darkMode ? 'dark' : 'light');
    }
  }, [colorsettings.pinyinColors, colorsettings.charColors, uisettings.darkMode, mainColor]);
  const optimizeWord = useCallback((word, index) => {
    if (!word || typeof word !== 'object') return null;

    // Determine the actual character value based on char_type
    let char;
    if (props.char_type === 'pinyin') {
      // Try different possible pinyin property names
      char = word.pinyinMarks || word.pinyin;
    } else {
      // For simplified/traditional, use the property name directly
      char = word[props.char_type];
    }
    return {
      simplified: word.simplified,
      traditional: word.traditional,
      simptradsame: word.simptradsame,
      id: word.id || `${props.char_type}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      char: char,
      // Use the resolved character
      skill: word.skill || '1',
      color: mapToCharType(word, props.char_type)
    };
  }, [props.char_type, mapToCharType]);
  const optimizedWords = useMemo(() => {
    if (!props.word || Object.keys(props.word).length === 0) return [];
    const fromDictionary = props.word.fromDictionary || props.word.from_dictionary;
    if (Array.isArray(fromDictionary) && fromDictionary.length > 0) {
      return fromDictionary.map((word, index) => optimizeWord(word, index)).filter(Boolean);
    } else {
      const optimized = optimizeWord(props.word, 0);
      return optimized ? [optimized] : [];
    }
  }, [props.word, optimizeWord]);
  if (optimizedWords.length === 0) {
    return /*#__PURE__*/React.createElement(Stack, null);
  }

  //console.log('otpimized words', optimizedWords);

  return /*#__PURE__*/React.createElement(XStack, {
    flexWrap: "wrap",
    alignItems: "center",
    mx: props.charSpace ? uisettings.charSpaceVal : 0
  }, optimizedWords.map((word, index) => /*#__PURE__*/React.createElement(Pressable, {
    key: word.id,
    disabled: notClickable(word.char),
    onPress: () => props.char_select && props.char_select(word),
    index: index
  }, /*#__PURE__*/React.createElement(Stack, {
    alignItems: "center",
    paddingRight: props.char_type === 'pinyin' ? 4 : 0
  }, /*#__PURE__*/React.createElement(Text, {
    style: {
      userSelect: Platform.OS === 'web' ? 'none' : null,
      fontSize: props.fontSize,
      alignItems: 'center',
      color: word.color,
      fontFamily: fontFamilyDecider(),
      height: props.char_type === 'pinyin' ? undefined : 7 + props.fontSize
    }
  }, word.char), !dashDisabled() && /*#__PURE__*/React.createElement(HSKDash, {
    skill: props.word.skill
  }, "\u2582")))));
};
export default /*#__PURE__*/React.memo(TonedChars);